/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { projectDailySales, projectTotalSales } from './projection';
import { KpiCard, DateStepper, ChartCard, StatusPill, SegmentedPills, PrimaryBtn, SecondaryBtn, DestructiveBtn, rangeForPeriod, priorPeriodRange } from './components/systemKit';

// Single authoritative place to enter sales. Reads / writes labor_sales (the same table
// used by Labor Costs and the Operations Overview) so every dashboard stays in sync.
// Supports daily entry with optional lunch/dinner/bar breakdown, plus a one-input
// weekly total for restaurants that don't track per-shift revenue.

const DAY_OF_WEEK_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function fmtDate(d) { return d.toISOString().split('T')[0]; }
function fmt$(n) { return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmt$Short(n) { return `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`; }

function getWeekDates(offset = 0, weekStartDay = 0) {
  const now = new Date();
  const today = now.getDay();
  const backToStart = (today - weekStartDay + 7) % 7;
  const start = new Date(now);
  start.setDate(now.getDate() - backToStart + offset * 7);
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}

export default function Sales({ companyId }) {
  const [weekStartDay, setWeekStartDay] = useState(0);
  // Period-aware date control at the top of the page — drives summary KPIs,
  // total/avg/best stats, and the trend chart. See DESIGN_SYSTEM.md
  // §Period-aware DateStepper.
  const [period, setPeriod] = useState('week');
  const [range, setRange] = useState(() => rangeForPeriod('week', new Date()));
  // weekOffset is preserved separately for the daily entry grid below the
  // summary section. Per Option A in the unification plan, the entry grid
  // stays Week-locked regardless of selected period — daily entry is a
  // weekly workflow that doesn't translate to longer windows.
  const [weekOffset, setWeekOffset] = useState(0);
  const [sales, setSales] = useState({}); // { dateStr: row } — current week (entry grid)
  const [periodSales, setPeriodSales] = useState({}); // { dateStr: row } — full range (summary)
  const [periodPriorTotal, setPeriodPriorTotal] = useState(0);
  const [trend, setTrend] = useState([]); // last 12 weeks (always 12-week buckets)
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState('');
  // Projected weekly total — sum of per-day projections (same DOW × 4 weeks history,
  // scaled by the recent-vs-prior trend factor). Null when we have no history yet.
  const [projectedWeekTotal, setProjectedWeekTotal] = useState(null);
  // Per-day projection map for THIS week's daily grid. Each value is the resolved projection
  // (manual override if set, else auto). overridesByDate tells us which days are manual.
  const [projectedByDate, setProjectedByDate] = useState({});

  // Daily entry modal
  const [entering, setEntering] = useState(null); // { date, dateStr, day }
  const [entryMode, setEntryMode] = useState('quick'); // 'quick' | 'breakdown'
  const [totalVal, setTotalVal] = useState('');
  const [lunchVal, setLunchVal] = useState('');
  const [dinnerVal, setDinnerVal] = useState('');
  const [barVal, setBarVal] = useState('');
  // Manual projection override for the day being edited. Empty string = "use auto from history".
  const [projectedVal, setProjectedVal] = useState('');

  // Weekly quick-entry modal
  const [showWeekly, setShowWeekly] = useState(false);
  const [weeklyVal, setWeeklyVal] = useState('');
  const [weeklyOffset, setWeeklyOffset] = useState(0);

  const weekDates = getWeekDates(weekOffset, weekStartDay);
  const weekLabel = `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data } = await supabase
        .from('food_cost_settings')
        .select('week_start_day')
        .eq('company_id', companyId)
        .maybeSingle();
      if (data) setWeekStartDay(Number(data.week_start_day) || 0);
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    fetchData();
  }, [companyId, weekOffset, weekStartDay, range.start, range.end]);

  async function fetchData() {
    setLoading(true);
    const ds = weekDates.map(fmtDate);
    // 12-week trend window ending at the current selected week. The trend
    // chart stays at 12-week granularity regardless of period — it's a
    // separate "long-arc context" surface.
    const trendStartDate = new Date(weekDates[0]); trendStartDate.setDate(trendStartDate.getDate() - 7 * 11);
    const trendStart = fmtDate(trendStartDate);
    const trendEnd = ds[6];
    // Period-range fetch covers the summary section above the entry grid.
    const prior = priorPeriodRange(period, range);
    const [weekRes, trendRes, periodRes, priorRes] = await Promise.all([
      supabase.from('labor_sales').select('*').eq('company_id', companyId).in('date', ds),
      supabase.from('labor_sales').select('date, total_sales').eq('company_id', companyId).gte('date', trendStart).lte('date', trendEnd),
      supabase.from('labor_sales').select('date, total_sales, is_weekly_total').eq('company_id', companyId).gte('date', range.start).lte('date', range.end),
      supabase.from('labor_sales').select('total_sales').eq('company_id', companyId).gte('date', prior.start).lte('date', prior.end),
    ]);
    const sm = {}; (weekRes.data || []).forEach(r => { sm[r.date] = r; });
    setSales(sm);
    const psm = {}; (periodRes.data || []).forEach(r => { psm[r.date] = r; });
    setPeriodSales(psm);
    setPeriodPriorTotal((priorRes.data || []).reduce((s, r) => s + Number(r.total_sales || 0), 0));

    // Bucket trend into 12 week-buckets keyed by week-start date.
    const weeks = [];
    for (let i = 11; i >= 0; i--) {
      const wd = getWeekDates(weekOffset - i, weekStartDay);
      weeks.push({ start: fmtDate(wd[0]), end: fmtDate(wd[6]), label: wd[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) });
    }
    const trendData = weeks.map(w => {
      const total = (trendRes.data || []).filter(r => r.date >= w.start && r.date <= w.end).reduce((s, r) => s + Number(r.total_sales || 0), 0);
      return { ...w, total };
    });
    setTrend(trendData);

    // Build the per-day projection for THIS week. The 12-week trend window already gives us
    // ~84 days of history, which is enough for the same-DOW + trend-factor math (needs 8w).
    const salesByDate = {};
    (trendRes.data || []).forEach(r => { salesByDate[r.date] = Number(r.total_sales || 0); });
    // Manual overrides for the current week — pulled from the labor_sales rows we already
    // fetched. Operators set these per-day from the entry modal when they expect a deviation.
    const overrides = {};
    Object.values(sm).forEach(row => {
      if (row.projected_sales != null) overrides[row.date] = Number(row.projected_sales);
    });
    const dailyMap = projectDailySales(ds, salesByDate, overrides);
    setProjectedByDate(dailyMap);
    const projTotal = projectTotalSales(ds, salesByDate, overrides);
    // Only show projection when we actually had any history to base it on (or any manual entries).
    setProjectedWeekTotal(projTotal > 0 ? projTotal : null);

    setLoading(false);
  }

  function openDayEntry(day) {
    const ds = fmtDate(day);
    const existing = sales[ds];
    setEntering({ date: day, dateStr: ds, day });
    setEntryMode(existing && (existing.lunch_sales || existing.dinner_sales || existing.bar_sales) ? 'breakdown' : 'quick');
    setTotalVal(existing?.total_sales ? String(existing.total_sales) : '');
    setLunchVal(existing?.lunch_sales ? String(existing.lunch_sales) : '');
    setDinnerVal(existing?.dinner_sales ? String(existing.dinner_sales) : '');
    setBarVal(existing?.bar_sales ? String(existing.bar_sales) : '');
    // Manual override only — empty means "use auto from history".
    setProjectedVal(existing?.projected_sales != null ? String(existing.projected_sales) : '');
  }

  async function saveDayEntry() {
    if (!entering) return;
    let total, lunch, dinner, bar;
    if (entryMode === 'quick') {
      total = parseFloat(totalVal) || 0;
      lunch = 0; dinner = 0; bar = 0;
    } else {
      lunch = parseFloat(lunchVal) || 0;
      dinner = parseFloat(dinnerVal) || 0;
      bar = parseFloat(barVal) || 0;
      total = lunch + dinner + bar;
    }
    // Allow saving projection-only updates (no actuals yet) so an operator can pre-set a
    // forecast for an upcoming busy day. Either total OR projected has to be present.
    const projectedNum = parseFloat(projectedVal);
    const hasProjection = projectedVal !== '' && !isNaN(projectedNum) && projectedNum >= 0;
    if (total <= 0 && !hasProjection) return;
    // Explicit is_weekly_total=false: if the user is now entering a specific day, the row is
    // a daily entry even if a prior weekly total was stored on this same (week-start) date.
    const row = {
      company_id: companyId,
      date: entering.dateStr,
      lunch_sales: lunch,
      dinner_sales: dinner,
      bar_sales: bar,
      total_sales: total,
      is_weekly_total: false,
      // null clears the override → auto projection takes over.
      projected_sales: hasProjection ? projectedNum : null,
    };
    await supabase.from('labor_sales').upsert([row], { onConflict: 'company_id,date' });
    setEntering(null);
    flash('Sales saved');
    fetchData();
  }

  async function deleteDayEntry() {
    if (!entering) return;
    if (!window.confirm('Delete this day\u2019s sales?')) return;
    await supabase.from('labor_sales').delete().eq('company_id', companyId).eq('date', entering.dateStr);
    setEntering(null);
    flash('Deleted');
    fetchData();
  }

  async function saveWeeklyTotal() {
    const total = parseFloat(weeklyVal);
    if (!total || total <= 0) return;
    const wd = getWeekDates(weeklyOffset, weekStartDay);
    const startDate = fmtDate(wd[0]);
    // Store weekly total on the week-start day, flagged so the UI can render a "whole-week"
    // summary instead of making it look like a single-day blowout. Other readers still just
    // sum total_sales across 7 days and get the right answer.
    await supabase.from('labor_sales').upsert(
      [{ company_id: companyId, date: startDate, total_sales: total, is_weekly_total: true, lunch_sales: 0, dinner_sales: 0, bar_sales: 0 }],
      { onConflict: 'company_id,date' }
    );
    setShowWeekly(false);
    setWeeklyVal('');
    flash('Weekly total saved');
    fetchData();
  }

  async function clearWeeklyTotal() {
    if (!window.confirm('Remove this weekly total so you can enter day-by-day sales?')) return;
    const wd = getWeekDates(weekOffset, weekStartDay);
    await supabase.from('labor_sales').delete().eq('company_id', companyId).eq('date', fmtDate(wd[0])).eq('is_weekly_total', true);
    flash('Cleared');
    fetchData();
  }

  function flash(msg) { setSaved(msg); setTimeout(() => setSaved(''), 2000); }

  const weekStartStr = fmtDate(weekDates[0]);
  const weeklyRow = sales[weekStartStr]?.is_weekly_total ? sales[weekStartStr] : null;
  const weekTotal = weekDates.reduce((s, d) => s + (Number(sales[fmtDate(d)]?.total_sales) || 0), 0);

  // Period summary stats — drive the KPIs above the entry grid. Computed
  // over [range.start, range.end] from periodSales (separate fetch from the
  // entry grid's per-week data).
  const periodTotal = Object.values(periodSales).reduce((s, r) => s + Number(r.total_sales || 0), 0);
  const periodDaysWithData = Object.values(periodSales).filter(r => Number(r.total_sales || 0) > 0).length;
  const periodAvgPerDay = periodDaysWithData > 0 ? periodTotal / periodDaysWithData : null;
  const periodBestDay = Object.values(periodSales).reduce(
    (max, r) => Number(r.total_sales || 0) > max ? Number(r.total_sales || 0) : max,
    0
  );
  const periodDelta = periodPriorTotal > 0 ? periodTotal - periodPriorTotal : null;
  const periodDeltaPct = periodPriorTotal > 0 ? (periodDelta / periodPriorTotal) * 100 : null;
  const periodNoun = period === 'week' ? 'week' : period === 'month' ? 'month' : period === 'quarter' ? 'quarter' : 'year';
  const prevLabel = `vs Last ${periodNoun.charAt(0).toUpperCase() + periodNoun.slice(1)}`;

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Sales</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>One place to log sales. {DAY_OF_WEEK_LABELS[weekStartDay]}–{DAY_OF_WEEK_LABELS[(weekStartDay + 6) % 7]} weeks.</p>
          </div>
          <div className="flex items-center gap-2">
            {saved && <StatusPill tone="green">{'\u2713'} {saved}</StatusPill>}
            <SecondaryBtn
              onClick={() => { setShowWeekly(true); setWeeklyOffset(weekOffset); setWeeklyVal(''); }}
            >
              + Weekly Total
            </SecondaryBtn>
          </div>
        </div>

        {/* Period-aware DateStepper — canonical date control. Drives the
            summary KPIs below. The daily entry grid further down keeps its
            own week-stepper. See DESIGN_SYSTEM.md §Period-aware DateStepper. */}
        <div className="flex items-center justify-end mb-6">
          <DateStepper
            period={period}
            onPeriodChange={setPeriod}
            range={range}
            onRangeChange={setRange}
            alignment="right"
          />
        </div>

        {/* Period summary — totals, avg per day, best single day, vs prior. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            label={`${periodNoun.charAt(0).toUpperCase() + periodNoun.slice(1)} Total`}
            value={periodTotal > 0 ? fmt$Short(periodTotal) : '\u2014'}
            secondary={`${periodDaysWithData} ${periodDaysWithData === 1 ? 'day' : 'days'} entered`}
          />
          <KpiCard
            label="Avg / Day"
            value={periodAvgPerDay != null ? fmt$Short(periodAvgPerDay) : '\u2014'}
            secondary="across days with data"
          />
          <KpiCard
            label="Best Day"
            value={periodBestDay > 0 ? fmt$Short(periodBestDay) : '\u2014'}
            secondary="Top single day"
          />
          <KpiCard
            label={prevLabel}
            value={periodDelta != null ? `${periodDelta >= 0 ? '\u2191' : '\u2193'} ${fmt$Short(Math.abs(periodDelta))}` : '\u2014'}
            valueTone={periodDelta != null ? (periodDelta >= 0 ? 'success' : 'critical') : 'neutral'}
            secondary={periodDeltaPct != null ? `${periodDeltaPct.toFixed(1)}% change` : `No data last ${periodNoun}`}
          />
        </div>

        {/* ── Daily entry section ──────────────────────────────────────────
            Daily-grid entry is fundamentally a weekly workflow (same-DOW
            projections, weekday breakdown, "Enter sales for [day]"). It's
            kept here as its own section regardless of which period is
            selected above. The week-stepper navigates which week to enter,
            independent of the summary period. */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Enter sales by day
            <span className="text-xs ml-2" style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
              {`Daily entries for ${weekLabel}`}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {projectedWeekTotal != null && (
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Projected this week: <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt$Short(projectedWeekTotal)}</span>
              </span>
            )}
            <DateStepper
              label={weekLabel}
              onPrev={() => setWeekOffset(w => w - 1)}
              onNext={() => setWeekOffset(w => w + 1)}
              onReset={weekOffset !== 0 ? () => setWeekOffset(0) : undefined}
            />
          </div>
        </div>

        {/* Weekly-mode banner: replaces the daily grid when the whole week was entered at once.
            Clearing lets the user switch back to day-by-day detail. */}
        {weeklyRow ? (
          <div className="rounded-lg p-5 mb-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <StatusPill tone="blue">Weekly total</StatusPill>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Entered for the entire week</span>
                </div>
                <div className="text-3xl font-bold mt-2" style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>{fmt$Short(Number(weeklyRow.total_sales))}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{weekLabel}</div>
              </div>
              <div className="flex items-center gap-2">
                <SecondaryBtn
                  onClick={() => { setShowWeekly(true); setWeeklyOffset(weekOffset); setWeeklyVal(String(Number(weeklyRow.total_sales))); }}
                  className="!px-3 !py-1.5 !text-sm"
                >
                  Edit
                </SecondaryBtn>
                <DestructiveBtn onClick={clearWeeklyTotal} className="!px-3 !py-1.5 !text-sm">
                  Clear & enter day-by-day
                </DestructiveBtn>
              </div>
            </div>
          </div>
        ) : (
        <div className="rounded-lg overflow-hidden mb-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          {/* Daily grid columns: Day | Projected | Total | Lunch | Dinner | Bar | action.
             Projected lives next to Total so the operator can eyeball pace day-by-day. */}
          <div className="grid" style={{ gridTemplateColumns: '140px 1fr 1fr 1fr 1fr 1fr 80px', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
            {['Day', 'Projected', 'Total', 'Lunch', 'Dinner', 'Bar', ''].map(h => (
              <div key={h} className="px-4 py-2.5" style={{ fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>{h}</div>
            ))}
          </div>
          {weekDates.map((d, di) => {
            const ds = fmtDate(d);
            const row = sales[ds];
            const hasBreakdown = row && (row.lunch_sales || row.dinner_sales || row.bar_sales);
            const isToday = fmtDate(new Date()) === ds;
            const projected = projectedByDate[ds];
            const isManual = row?.projected_sales != null;
            return (
              <div
                key={ds}
                className="grid cursor-pointer transition-colors"
                style={{
                  gridTemplateColumns: '140px 1fr 1fr 1fr 1fr 1fr 80px',
                  borderBottom: di < weekDates.length - 1 ? '1px solid var(--border)' : 'none',
                  background: isToday ? 'var(--accent-subtle)' : 'transparent',
                  minHeight: 52,
                }}
                onClick={() => openDayEntry(d)}
                onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = 'var(--bg-subtle)'; }}
                onMouseLeave={e => { if (!isToday) e.currentTarget.style.background = 'transparent'; }}
              >
                <div className="px-4 py-3">
                  <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {d.toLocaleDateString('en-US', { weekday: 'short' })}
                    <span className="font-normal ml-1" style={{ color: 'var(--text-muted)' }}>{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
                {/* Projected: bold text when manually overridden, lighter "auto" styling when
                   computed from history. "auto" rendered as tiny inline micro-text per systemKit
                   comment — NOT a StatusPill (which is standardized at 11/500). */}
                <div className="px-4 py-3 text-sm" style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {projected != null ? (
                    <span style={{ color: isManual ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: isManual ? 500 : 400 }}>
                      {fmt$(projected)}
                      {!isManual && <span className="ml-1" style={{ fontSize: 10, color: 'var(--text-muted)' }}>auto</span>}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>
                  )}
                </div>
                <div className="px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  {row && row.total_sales > 0 ? fmt$(row.total_sales) : <span className="text-xs" style={{ color: 'var(--text-muted)' }}>tap to enter</span>}
                </div>
                <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{hasBreakdown && row.lunch_sales > 0 ? fmt$(row.lunch_sales) : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}</div>
                <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{hasBreakdown && row.dinner_sales > 0 ? fmt$(row.dinner_sales) : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}</div>
                <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{hasBreakdown && row.bar_sales > 0 ? fmt$(row.bar_sales) : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}</div>
                <div className="px-4 py-3">
                  <span
                    className="text-xs"
                    style={{ color: row ? 'var(--text-secondary)' : 'var(--accent-primary)', fontWeight: row ? 400 : 600 }}
                  >
                    {row ? 'Edit' : '+ Add'}
                  </span>
                </div>
              </div>
            );
          })}
          <div className="grid" style={{ gridTemplateColumns: '140px 1fr 1fr 1fr 1fr 1fr 80px', background: 'var(--bg-subtle)', borderTop: '2px solid var(--border)' }}>
            <div className="px-4 py-3" style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>Week</div>
            <div className="px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{projectedWeekTotal != null ? fmt$(projectedWeekTotal) : '\u2014'}</div>
            <div className="px-4 py-3 text-sm font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{fmt$(weekTotal)}</div>
            <div /><div /><div /><div />
          </div>
        </div>
        )}

        {/* Trend */}
        <ChartCard title="12-week trend">
          {trend.filter(t => t.total > 0).length < 2 ? (
            <div className="py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Enter a few weeks of sales to see the trend.</div>
          ) : (
            <SalesTrend rows={trend} />
          )}
        </ChartCard>

        {loading && <div className="text-center text-xs mt-4" style={{ color: 'var(--text-muted)' }}>Loading…</div>}
      </div>

      {/* Daily entry modal */}
      {entering && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div className="rounded-lg p-6 w-full max-w-sm mx-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{entering.date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Enter total sales, or break down by lunch / dinner / bar.</div>
              </div>
              <button onClick={() => setEntering(null)} className="text-lg px-1 rounded-md transition-colors" style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
              >{'\u2715'}</button>
            </div>

            <div className="mb-4 flex">
              <SegmentedPills
                value={entryMode}
                onChange={setEntryMode}
                options={[
                  { key: 'quick', label: 'Quick total' },
                  { key: 'breakdown', label: 'Lunch / Dinner / Bar' },
                ]}
                size="sm"
              />
            </div>

            {entryMode === 'quick' ? (
              <div className="mb-2">
                <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Total sales</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl" style={{ color: 'var(--text-muted)' }}>$</span>
                  <input type="number" step="0.01" value={totalVal} onChange={e => setTotalVal(e.target.value)}
                    autoFocus placeholder="0.00"
                    className="w-full rounded-lg pl-10 pr-4 py-4 text-2xl font-bold focus:outline-none"
                    style={{ color: 'var(--text-primary)', background: 'var(--bg-subtle)', border: '2px solid var(--border)' }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3 mb-2">
                {[
                  { label: 'Lunch', val: lunchVal, set: setLunchVal },
                  { label: 'Dinner', val: dinnerVal, set: setDinnerVal },
                  { label: 'Bar', val: barVal, set: setBarVal },
                ].map(r => (
                  <div key={r.label}>
                    <label className="block mb-1" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>{r.label}</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>$</span>
                      <input type="number" step="0.01" value={r.val} onChange={e => r.set(e.target.value)}
                        placeholder="0.00"
                        className="w-full rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none"
                        style={{ color: 'var(--text-primary)', background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
                        onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                        onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                    </div>
                  </div>
                ))}
                <div className="text-xs pt-2" style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border)' }}>
                  Total: <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{fmt$((parseFloat(lunchVal) || 0) + (parseFloat(dinnerVal) || 0) + (parseFloat(barVal) || 0))}</span>
                </div>
              </div>
            )}

            {/* Optional projection override — leave blank to use the auto same-DOW forecast.
               Useful when the operator knows about a specific event (holiday, weather, concert)
               that the historical pattern can't see. */}
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
              <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Projected sales for this day <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>$</span>
                <input type="number" step="0.01" value={projectedVal} onChange={e => setProjectedVal(e.target.value)}
                  placeholder={projectedByDate[entering.dateStr] != null ? `Auto: ${fmt$(projectedByDate[entering.dateStr])}` : '0.00'}
                  className="w-full rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none"
                  style={{ color: 'var(--text-primary)', background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
              <div className="mt-1" style={{ fontSize: 11, color: 'var(--text-muted)' }}>Leave blank to use the auto forecast (same weekday × last 4 weeks).</div>
            </div>

            <div className="flex gap-2 mt-4">
              {sales[entering.dateStr] && (
                <DestructiveBtn onClick={deleteDayEntry}>Delete</DestructiveBtn>
              )}
              <PrimaryBtn onClick={saveDayEntry} className="flex-1">Save</PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      {/* Weekly total modal */}
      {showWeekly && (() => {
        const wd = getWeekDates(weeklyOffset, weekStartDay);
        const weeklyLabel = `${wd[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} \u2013 ${wd[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <div className="rounded-lg p-6 w-full max-w-sm mx-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <div className="flex items-center justify-between mb-5">
                <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Weekly Sales</div>
                <button onClick={() => setShowWeekly(false)} className="text-lg px-1 rounded-md transition-colors" style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
                >{'\u2715'}</button>
              </div>
              <div className="mb-4">
                <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Week</label>
                <DateStepper
                  label={weeklyLabel}
                  onPrev={() => setWeeklyOffset(o => o - 1)}
                  onNext={() => setWeeklyOffset(o => o + 1)}
                />
              </div>
              <div>
                <label className="block mb-1.5" style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Total sales for the week</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl" style={{ color: 'var(--text-muted)' }}>$</span>
                  <input type="number" step="0.01" value={weeklyVal} onChange={e => setWeeklyVal(e.target.value)}
                    autoFocus placeholder="0.00"
                    className="w-full rounded-lg pl-10 pr-4 py-4 text-2xl font-bold focus:outline-none"
                    style={{ color: 'var(--text-primary)', background: 'var(--bg-subtle)', border: '2px solid var(--border)' }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
                <div className="mt-1.5" style={{ fontSize: 12, color: 'var(--text-muted)' }}>Stored on the week-start day. If you later enter daily values they'll override this.</div>
              </div>
              <PrimaryBtn onClick={saveWeeklyTotal} disabled={!weeklyVal || parseFloat(weeklyVal) <= 0} className="w-full mt-4 !py-3">
                Save
              </PrimaryBtn>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// (ProjectedCard removed — projection is now rendered inline in the daily
// entry section header. See period unification commit.)

function SalesTrend({ rows }) {
  const W = 640;
  const H = 180;
  const padL = 42, padR = 12, padT = 10, padB = 24;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const maxY = Math.max(100, ...rows.map(r => r.total));
  const xAt = (i) => padL + (rows.length <= 1 ? plotW / 2 : (i / (rows.length - 1)) * plotW);
  const yAt = (v) => padT + plotH - (v / maxY) * plotH;
  const path = rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(r.total).toFixed(1)}`).join(' ');
  const yTicks = [0, maxY / 2, maxY].map(v => Math.round(v));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="180" preserveAspectRatio="none">
      {yTicks.map(v => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={yAt(v)} y2={yAt(v)} stroke="var(--border)" strokeWidth="1" />
          <text x={padL - 6} y={yAt(v) + 3} fontSize="9" fill="var(--text-muted)" textAnchor="end">${Math.round(v).toLocaleString()}</text>
        </g>
      ))}
      {/* Data series — purple is this app's brand/primary, keeping hardcoded per Data Colors. */}
      <path d={path} fill="none" stroke="#4F46E5" strokeWidth="2.5" />
      {rows.map((r, i) => (
        <g key={i}>
          <circle cx={xAt(i)} cy={yAt(r.total)} r="3" fill="#4F46E5" />
          {i % 2 === 0 && <text x={xAt(i)} y={H - 6} fontSize="9" fill="var(--text-muted)" textAnchor="middle">{r.label}</text>}
        </g>
      ))}
    </svg>
  );
}
